import {
  Banner,
  Button,
  DropZone,
  FormLayout,
  LegacyStack,
  List,
  Modal,
  Spinner,
  Text,
  TextField,
} from "@shopify/polaris";
import React, { useCallback, useContext, useState } from "react";
import { useHistory } from "react-router";
import { ContextUser } from "src/context/ContextUser";
import {
  usePayTransitDisbursementMutation,
  useSupplierInternalDisbursementListQuery,
  useUploadMutation,
} from "src/graphql/codegen_graphql";
import { Toast } from "src/hooks/toast";

export function UploadInternalTransferProof() {
  const { merchant } = useContext(ContextUser);
  const { location, push } = useHistory();
  const q = new URLSearchParams(location.search);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File>();
  const [url, setUrl] = useState("");
  const [rejectedFiles, setRejectedFiles] = useState<File[]>([]);
  const hasError = rejectedFiles.length > 0;
  const [feeInput, setFeeInput] = useState(0);
  const [exchangeRateInput, setExchangeRateInput] = useState(0);
  const [errorInput, setErrorInput] = useState<any>(null);
  const { data, loading } = useSupplierInternalDisbursementListQuery({
    skip: !q.get("id"),
    variables: {
      supplierInternalDisbursementListId: q.get("id"),
      supplierId: merchant.id,
    },
  });

  const [upload, propsUpload] = useUploadMutation();
  const [payTransit, propspayTransit] = usePayTransitDisbursementMutation({
    refetchQueries: ["supplierInternalDisbursementList", "supplierDisbursementDetail"],
  });

  // const validImageTypes = ["image/jpeg", "image/png"];

  const toggleActive = useCallback(() => setOpen(!open), [open]);

  const handleDropZoneDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => {
      if (acceptedFiles[0]) {
        upload({
          variables: {
            file: acceptedFiles[0],
          },
        }).then((res) => {
          if (res.data?.upload) {
            setFile(acceptedFiles[0]);
            setUrl(res.data.upload.url + "");
          }
        });
      }
      setRejectedFiles(_rejectedFiles);
    },
    [upload],
  );

  const handleUploadInternal = useCallback(() => {
    if (!exchangeRateInput) {
      Toast.onShow("Exchange rate is required!", "danger", "right");
      setErrorInput({
        type: "exchangeRate",
        text: "Exchange rate is required!",
      });
      return;
    }

    if (!url) {
      Toast.onShow("Proof is required!", "danger", "right");
      setErrorInput({
        type: "proof",
        text: "Proof is required!",
      });
      return;
    }

    setErrorInput(null);
    const input = {
      fee: Number(feeInput),
      exchangeRate: Number(exchangeRateInput),
      proof: url,
      disburseId: Number(q.get("id")?.replace("DBS", "")),
    };

    payTransit({
      variables: {
        ...input,
      },
    })
      .then((res) => {
        if (res.data?.payTransitDisbursement) {
          Toast.onShow("Pay transit disbursement was done.", "success", "right");
          setFile(undefined);
          setUrl("");
          setFeeInput(0);
          setExchangeRateInput(0);
          setOpen(false);
          push("/applications/disbursement/request");
        } else {
          Toast.onShow("Something was wrong please try again!", "danger", "right");
        }
      })
      .catch(() => {
        Toast.onShow("Something was wrong please try again!", "danger", "right");
      });
  }, [feeInput, exchangeRateInput, url, q, payTransit, push]);

  const internal = data
    ? data?.supplierInternalDisbursementList?.data
      ? data?.supplierInternalDisbursementList?.data[0]
      : null
    : null;

  if (loading && !internal) {
    return <></>;
  }

  if (internal?.publicStatus !== "IN_TRANSIT") {
    return <></>;
  }

  const activator = (
    <div style={{ display: "flex", justifyContent: "end" }}>
      <Button variant="primary" onClick={toggleActive}>
        Upload internal transfer proof
      </Button>
    </div>
  );

  const fileUpload = !file && !propsUpload.loading && (
    <DropZone.FileUpload actionTitle="Add proof" actionHint="Accepts .jpg, and .png" />
  );
  const fileUploading = !file && propsUpload.loading && (
    <LegacyStack vertical alignment="center">
      <LegacyStack alignment="center">
        <Spinner size="small" />
      </LegacyStack>
    </LegacyStack>
  );
  const uploadedFile = file && !propsUpload.loading && (
    <LegacyStack vertical alignment="center">
      <LegacyStack alignment="center">
        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <div style={{ width: "30%", maxHeight: 118, overflow: "hidden" }}>
            <img alt="" src={url} style={{ width: "100%" }} />
          </div>
        </div>
      </LegacyStack>
    </LegacyStack>
  );

  const errorMessage = hasError && (
    <Banner title="The following images couldn’t be uploaded:" tone="critical">
      <List type="bullet">
        {rejectedFiles.map((file, index) => (
          <List.Item key={index}>{`"${file.name}" is not supported. File type must be .jpg, or .png.`}</List.Item>
        ))}
      </List>
    </Banner>
  );

  return (
    <div>
      <Modal
        title="Upload internal transfer proof"
        activator={activator}
        open={open}
        onClose={() => {
          if (!propspayTransit.loading) {
            setFile(undefined);
            setUrl("");
            setFeeInput(0);
            setExchangeRateInput(0);
            toggleActive();
          }
        }}
        primaryAction={{
          content: "Upload",
          onAction: handleUploadInternal,
          loading: propspayTransit.loading,
          disabled: !file || exchangeRateInput <= 0 || propspayTransit.loading,
        }}
      >
        <Modal.Section>
          {errorMessage}
          <DropZone
            error={errorInput && errorInput.type === "proof" && errorInput.text}
            errorOverlayText={errorInput && errorInput.type === "proof" && errorInput.text}
            accept="image/*"
            type="image"
            onDrop={handleDropZoneDrop}
            allowMultiple={false}
          >
            {uploadedFile}
            {fileUpload}
            {fileUploading}
          </DropZone>
        </Modal.Section>
        <Modal.Section>
          <FormLayout>
            <FormLayout.Group>
              <TextField
                label="Fee"
                type="number"
                autoComplete="off"
                placeholder="Enter fee value"
                value={feeInput + ""}
                onChange={(v) => setFeeInput(Number(v))}
              />
              <TextField
                requiredIndicator
                label="Exchange Rate"
                autoComplete="off"
                placeholder="Enter exchange rate value"
                value={exchangeRateInput + ""}
                type="number"
                error={errorInput && errorInput.type === "exchangeRate" && errorInput.text}
                onChange={(v) => setExchangeRateInput(Number(v))}
                prefix="[USD-RMB]"
              />
            </FormLayout.Group>
          </FormLayout>
        </Modal.Section>
      </Modal>
      <Text as="p" variant="bodySm" tone="critical">
        * This is for your internal transfer control. If you work with an agent, the agent has their own secure link to
        upload the proof from their side. If there is any issue between you and agent, please work directly with the
        agent to resolve the issue.
      </Text>
    </div>
  );
}
